import { PropertyConsumptionAddDialogComponent } from "@/components/property-consumption-add-dialog/property-consumption-add-dialog.component";
import { ObjectUpdatedType } from "@/enums/ObjectUpdatedType.enum";
import { TrackingHelper } from "@/helpers/tracking.helper";
import { UserHelper } from "@/helpers/user.helper";
import { AlertService } from "@/services/alert.service";
import { AppConfigService } from "@/services/app-config.service";
import { formatDate, NgFor, NgIf, NgStyle } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatRadioGroup, MatRadioModule } from "@angular/material/radio";
import {
  MatSlideToggle,
  MatSlideToggleModule,
} from "@angular/material/slide-toggle";
import {
  ConsumptionCapability,
  ConsumptionData,
  ConsumptionType,
  PropertyEvent,
  RealProperty,
  TimeStep,
  TrackingService,
} from "@eemo/api-client";
import {
  ChartType,
  GoogleChartComponent,
  GoogleChartsModule,
  ScriptLoaderService,
} from "angular-google-charts";
import { MdbPopoverModule } from "mdb-angular-ui-kit/popover";
import { Subscription } from "rxjs";

import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { MatButtonModule } from "@angular/material/button";

export class BarOverlay {
  iconStyle: any;
  barStyle: any;
  text: string[] = [];
}

@Component({
  selector: "app-tracking-consumption",
  templateUrl: "./tracking-consumption.component.html",
  styleUrls: ["./tracking-consumption.component.scss"],
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    MatSlideToggleModule,
    MatRadioModule,
    MatButtonModule,
    GoogleChartsModule,
    MatIconModule,
    NgStyle,
    MdbPopoverModule,
    TooltipDirective,
  ],
})
export class TrackingConsumptionComponent implements OnInit, OnDestroy {
  @Input() prop: RealProperty;
  @ViewChild("chart") chart: GoogleChartComponent;
  @ViewChild(MatRadioGroup)
  timeStepRadio: MatRadioGroup;
  @ViewChildren("consumptionTypes")
  consumptionTypesSliders: QueryList<MatSlideToggle>;

  sub = new Subscription();
  updateSub = new Subscription();

  chartLoaded = false;
  chartAxisTimeValues = [];

  capabilities: ConsumptionCapability[];
  selectedConsumptionTypes: ConsumptionType[] = [];
  selectedConsumptionUnits: string[] = [];
  selectedTimeStep = "YEAR";
  events: PropertyEvent[];
  eventsBar: BarOverlay[] = [];

  currentYear: number;
  yearRangeMin: number;
  yearRangeMax: number;
  yearRange: number[];

  chartPackagesLoaded = false;

  chartType = ChartType.ComboChart;
  columns: [];
  chartData: any = [];
  options = {
    animation: {
      duration: 1000,
      easing: "out",
    },
    legend: {
      position: "bottom",
    },
    pointSize: 5,
    height: 500,
    vAxes: {
      0: {
        title: "",
      },
      1: {
        title: "",
      },
    },
    series: {
      0: {
        targetAxisIndex: 0,
        type: "bars",
      },
      1: {
        targetAxisIndex: 1,
        type: "line",
      },
    },
  };

  constructor(
    private chartLoaderService: ScriptLoaderService,
    private trackingService: TrackingService,
    public trackingHelper: TrackingHelper,
    public userHelper: UserHelper,
    @Inject(LOCALE_ID) public locale: string,
    private dialog: MatDialog,
    private ref: ChangeDetectorRef,
    private alertService: AlertService,
    private appConfigService: AppConfigService
  ) {}

  ngOnInit() {
    this.loadData();
    this.updateSub = this.appConfigService.objectUpdated.subscribe((type) => {
      if (type === ObjectUpdatedType.COMSUMPTION) {
        this.loadData();
      }
    });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.updateSub.unsubscribe();
  }

  loadData() {
    this.sub = this.trackingService
      .getPropertyConsumptionCapablities(this.prop.uid)
      .subscribe((data) => {
        this.capabilities = data.sort(this.sortCapabilities);
        if (this.selectedConsumptionTypes.length === 0 && data.length > 0) {
          this.selectedConsumptionTypes.push(data[0].type);
        }
        this.initCapabilityYears();
        this.loadMap();
      });
  }

  initCapabilityYears() {
    this.yearRange = [];
    this.currentYear = 0;
    this.yearRangeMin = 10000;
    this.yearRangeMax = 0;
    if (this.selectedTimeStep === "YEAR") {
      this.currentYear = 1000;
      this.yearRangeMin = 0;
      this.yearRangeMax = 0;
    } else {
      this.capabilities
        .filter((capablity) =>
          this.selectedConsumptionTypes.includes(capablity.type)
        )
        .forEach((capability) => {
          const startYear = parseInt(capability.dataYearStart, 10);
          const endYear = parseInt(capability.dataYearEnd, 10);
          if (endYear > this.currentYear) {
            this.currentYear = endYear;
          }
          if (endYear > this.yearRangeMax) {
            this.yearRangeMax = endYear;
          }
          if (startYear < this.yearRangeMin) {
            this.yearRangeMin = startYear;
          }
        });
    }
    for (let i = this.yearRangeMin; i <= this.yearRangeMax; i++) {
      this.yearRange.push(i);
    }
  }

  loadMap() {
    if (this.capabilities.length === 0) {
      return;
    }
    this.trackingService
      .getPropertyConsumptions(
        this.prop.uid,
        "01-" + this.currentYear,
        TimeStep[this.selectedTimeStep],
        this.selectedConsumptionTypes,
        this.selectedTimeStep === "MONTH" ? "12-" + this.currentYear : null
      )
      .subscribe((consumptions) => {
        const chartData2: any[] = [];
        const dataTable: { [key: string]: number[][] } = {};
        const chartVAxes: any = {};
        const chartSeries: any = {};
        this.chartAxisTimeValues = [];

        // if their is only one consumption type selected the price is shown
        if (consumptions.length === 1) {
          // Only one consumption type is selected : the price is shown
          const data = consumptions[0];

          // check if a price exists
          const hasPrice =
            data.data.filter((detail) => detail.price).length > 0;
          chartVAxes["0"] = {
            title:
              "Consommation (" +
              this.trackingHelper.getConsumptionTypeUnit(data.type) +
              ")",
            minValue: 0,
          };
          chartSeries["0"] = {
            targetAxisIndex: 0,
            type: "bars",
            color: this.trackingHelper.getConsumptionLineColor(data.type),
            labelInLegend:
              this.trackingHelper.getConsumptionTypeName(data.type) +
              " (" +
              this.trackingHelper.getConsumptionTypeUnit(data.type) +
              ")",
          };
          chartSeries["1"] = {
            targetAxisIndex: 1,
            type: "line",
            color: this.trackingHelper.getConsumptionPriceLineColor(),
            labelInLegend: "Prix (€)",
          };

          if (hasPrice) {
            chartVAxes["1"] = {
              title: "Prix (€)",
              minValue: 0,
            };
          }
          data.data.forEach((detail) => {
            if (hasPrice) {
              chartData2.push([detail.period, detail.value, detail.price || 0]);
            } else {
              chartData2.push([detail.period, detail.value]);
            }
            this.chartAxisTimeValues.push(detail.period);
          });
        } else {
          // multiple consumptions types are selected
          const processedUnit = [];
          const dataList = consumptions.sort(this.sortData);
          let unitIndex = -1;
          for (let i = 0; i < dataList.length; i++) {
            const data = dataList[i];
            if (
              !processedUnit.includes(
                this.trackingHelper.getConsumptionTypeUnit(data.type)
              )
            ) {
              unitIndex++;
              processedUnit.push(
                this.trackingHelper.getConsumptionTypeUnit(data.type)
              );

              chartVAxes[unitIndex] = {
                title:
                  "Consommation (" +
                  this.trackingHelper.getConsumptionTypeUnit(data.type) +
                  ")",
                minValue: 0,
              };
            }

            chartSeries[i] = {
              targetAxisIndex: unitIndex,
              type: unitIndex === 0 ? "bars" : "line",
              color: this.trackingHelper.getConsumptionLineColor(data.type),
              labelInLegend:
                this.trackingHelper.getConsumptionTypeName(data.type) +
                " (" +
                this.trackingHelper.getConsumptionTypeUnit(data.type) +
                ")",
            };

            data.data.forEach((detail) => {
              // populate value with data details
              if (!dataTable[detail.period]) {
                dataTable[detail.period] = [];
                // prepare the array for each data type
                for (let i = 0; i < dataList.length; i++) {
                  dataTable[detail.period].push([]);
                }
              }
              dataTable[detail.period][i].push(detail.value);
              this.chartAxisTimeValues.push(detail.period);
            });
          }
          const periods = Object.keys(dataTable).sort(this.sortPeriod);

          let period: string;
          let val1: number;
          let val2: number;
          for (let i = 0; i < periods.length; i++) {
            const periodData = dataTable[periods[i]];
            period = periods[i];
            for (let j = 0; j < dataList.length; j++) {
              for (let k = 0; k < periodData[j].length; k++) {
                const curValue = periodData[j][k];
                const t = "";
                if (j === 0) {
                  val1 = curValue;
                } else {
                  val2 = curValue;
                }
              }
            }
            chartData2.push([period, val1, val2]);
          }
        }

        this.options.vAxes = chartVAxes;
        this.options.series = chartSeries;
        this.options = { ...this.options };
        this.chartData = chartData2;
      });
  }

  onChartReady() {
    this.eventsBar = [];
    this.ref.detectChanges();
    if (!this.chartLoaded) {
      this.loadGraphEventOverlays();
      this.chartLoaded = true;
    } else {
      setTimeout(() => {
        this.loadGraphEventOverlays();
      }, 1000);
    }
  }
  loadGraphEventOverlays() {
    if (!this.chart) {
      return;
    }
    const curEventsBar: { [key: string]: BarOverlay } = {};
    this.trackingService
      .getPropertyEvents(this.prop.uid, null, true)
      .subscribe((data) => {
        this.events = data.filter((event) =>
          this.selectedTimeStep === "YEAR"
            ? parseInt(formatDate(event.date, "yyyy", this.locale), 10) >=
              this.currentYear
            : formatDate(event.date, "yyyy", this.locale) ===
              String(this.currentYear)
        );

        const layout = (
          this.chart.chartWrapper.getChart() as any
        ).getChartLayoutInterface(); // @@@ as any bug typage d ela librairie
        const charAreaBounds = layout.getChartAreaBoundingBox();
        this.events.forEach((event) => {
          const value =
            this.selectedTimeStep === "YEAR"
              ? formatDate(event.date, "yyyy", this.locale)
              : formatDate(event.date, "MM-yyyy", this.locale);
          if (curEventsBar[value]) {
            // add the text to the item
            curEventsBar[value].text.push(
              formatDate(event.date, "dd-MM-yyyy", this.locale) +
                " - " +
                event.details
            );
          } else {
            const index = this.chartAxisTimeValues.indexOf(value);
            if (index < 0) {
              // the event is not in the graph date range
              return;
            }
            const x = layout.getXLocation(index);
            const offsetTop = (
              this.chart.chartWrapper.getChart().getContainer() as HTMLElement
            ).offsetTop;
            curEventsBar[value] = {
              iconStyle: {
                fontSize: "36px",
                cursor: "pointer",
                position: "absolute",
                top: charAreaBounds.top - 50 + offsetTop + "px",
                left: x - 17 + "px",
              },
              barStyle: {
                "border-left": "3px solid green",
                position: "absolute",
                top: charAreaBounds.top + offsetTop + "px",
                height: charAreaBounds.height + "px",
                left: x + "px",
              },
              text: [
                formatDate(event.date, "dd-MM-yyyy", this.locale) +
                  " - " +
                  event.details,
              ],
            };
          }
        });
        this.eventsBar = Object.values(curEventsBar);
        this.ref.detectChanges();
      });
  }

  timeStepChanged(event: any) {
    this.selectedTimeStep = this.timeStepRadio.selected.value;
    this.initCapabilityYears();
    this.loadMap();
  }

  consumptionTypesChanged(event: any) {
    if (
      this.consumptionTypesSliders.filter((slider) => slider.checked).length ===
      0
    ) {
      this.alertService.warning(
        "Au moins un type de consommation doit être sélectionné"
      );
      event.source.checked = true;
    } else {
      const tmpConsumptionTypes = [];
      const tmpConsumptionUnits = [];
      this.consumptionTypesSliders.forEach((slider) => {
        if (slider.checked) {
          const unit = this.trackingHelper.getConsumptionTypeUnit(
            ConsumptionType[slider.id]
          );
          tmpConsumptionTypes.push(ConsumptionType[slider.id]);
          if (!tmpConsumptionUnits.includes(unit)) {
            tmpConsumptionUnits.push(unit);
          }
        }
      });
      if (tmpConsumptionUnits.length > 2) {
        this.alertService.warning(
          "Seules 2 unités de consommations (kWh, m3...) sont autorisées"
        );
        event.source.checked = false;
      } else {
        this.selectedConsumptionTypes = tmpConsumptionTypes;
        this.selectedConsumptionUnits = tmpConsumptionUnits;
      }
      this.initCapabilityYears();
      this.loadMap();
    }
  }

  yearChanged(year: number) {
    this.currentYear = year;
    this.loadMap();
  }

  addConsumptions() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(
        PropertyConsumptionAddDialogComponent,
        {
          // width: '20rem'
          data: { prop: this.prop },
        }
      );

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.loadData();
        }
      });
    }
  }

  public sortCapabilities(
    a: ConsumptionCapability,
    b: ConsumptionCapability
  ): number {
    const types = TrackingHelper.getConsumptionTypesInOrder();
    const orderA = types.indexOf(a.type);
    const orderB = types.indexOf(b.type);
    if (orderA > orderB) {
      return 1;
    }
    if (orderA < orderB) {
      return -1;
    }
    return 0;
  }

  public sortData(a: ConsumptionData, b: ConsumptionData): number {
    const types = TrackingHelper.getConsumptionTypesInOrder();
    const orderA = types.indexOf(a.type);
    const orderB = types.indexOf(b.type);
    if (orderA > orderB) {
      return 1;
    }
    if (orderA < orderB) {
      return -1;
    }
    return 0;
  }

  public sortPeriod(a: string, b: string): number {
    const splitA = a.split("-");
    const splitB = b.split("-");
    const orderA = parseInt(splitA[1] + splitA[0], 10);
    const orderB = parseInt(splitB[1] + splitB[0], 10);
    if (orderA > orderB) {
      return 1;
    }
    if (orderA < orderB) {
      return -1;
    }
    return 0;
  }
}
